import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "@/shared/hooks/usePermission";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import { dashboardApi } from "../services/dashboard.api";
import type { AttendanceTrend, DashboardPayload, RiskyEmployee } from "../types";
import { formatYangilangan } from "../utils/davomatDarajasi";

/**
 * ⚠️⚠️ **POLLING, SSE EMAS** (kontrakt §7, spec R1). Arxitektura
 * spec §11: «avval TanStack Query polling (15–30 s), SSE faqat kerak
 * bo'lsa». 20 s — shu oraliqning o'rtasi.
 */
export const DASHBOARD_POLL_MS = 20_000;

/**
 * ⚠️ Xavfli xodimlar ro'yxati SEKINROQ poll qilinadi: u kunlik job
 * natijasi (`risk-score`, 04:00) va kun davomida faqat «ko'rib chiqdim»
 * belgisidan o'zgaradi — belgi esa mutatsiyaning O'ZI invalidatsiya
 * qiladi, ya'ni 20 s poll bu yerda faqat keraksiz yuk bo'lardi.
 */
export const RISKY_POLL_MS = 60_000;

/**
 * Sana BERILMAGAN holatning kesh kaliti.
 *
 * ⚠️ `undefined` ni kalit sifatida ishlatib bo'lmaydi — TanStack uni
 * `null` ga seriyalab, qo'lda tanlangan sana bilan chalkashtirardi.
 */
export const DASHBOARD_AUTO_KEY = "auto";

/**
 * Sanoqlar.
 *
 * ⚠️⚠️ **`date` IXTIYORIY va standart holatda YUBORILMAYDI.** Sana
 * berilmasa serverning o'zi SOZLANGAN zonadagi bugunni hisoblaydi
 * (`attendance.handlers.ts`, `AttendanceDashboardQuerySchema`). Klient
 * `dayjs()` bilan o'z BRAUZER zonasidan sana yuborsa, boshqa zonadagi
 * foydalanuvchi (VPN, UTC ga sozlangan kiosk) jimgina BOSHQA biznes-kunni
 * ko'rardi — CLAUDE.md `settings.timezone` bo'limi aynan shu sinf xatoni
 * ogohlantiradi.
 *
 * ⚠️ `enabled` — `attendance:read`. Ruxsatsiz foydalanuvchida so'rov
 * UMUMAN yuborilmasligi kerak: `refetchInterval` uni har 20 soniyada
 * 403 ga urardi va konsol shovqinga to'lardi.
 *
 * ⚠️ `refetchOnWindowFocus: "always"` — `true` EMAS. O'lchangan:
 * `shouldFetchOn` (`query-core` `queryObserver.js`) `true` ni FAQAT
 * so'rov eskirgan bo'lsa bajaradi, global `staleTime` esa 5 daqiqa
 * (`QueryProvider.tsx`) va 20 s poll `dataUpdatedAt` ni doim yangilab
 * turadi — ya'ni `true` bilan fokus refetch'i HECH QACHON ishlamasdi.
 * `"always"` esa `isStale` shartini chetlab o'tadi (`enabled` qorovuli
 * o'z joyida qoladi).
 *
 * ⚠️ **`refetchInterval: date ? false : DASHBOARD_POLL_MS`.** O'TGAN
 * kunning sanoqlari o'zgarmaydi — u yerda 20 s poll faqat yuk. Avto
 * rejim (sanasiz) esa poll qilinishda davom etadi.
 */
export function useDashboard(date?: string) {
	const { can } = usePermission();

	return useQuery<DashboardPayload>({
		queryKey: ["dashboard", date ?? DASHBOARD_AUTO_KEY],
		queryFn: () => dashboardApi.get(date),
		enabled: can("attendance", "read"),
		refetchInterval: date ? false : DASHBOARD_POLL_MS,
		refetchOnWindowFocus: "always",
	});
}

/**
 * Trend oynasining uzunligi.
 *
 * ⚠️ B kontrakti: `days` min 2, max 90
 * (`AttendanceTrendQuerySchema`). Konstanta KLIENTDA ham qulflanadi —
 * uni oshirgan kishi aks holda 422 olardi.
 */
export const TREND_DAYS = 30;

/**
 * ⚠️ Trendda `refetchInterval` YO'Q. Trend — KUNLIK seriya; 20 s poll
 * 30 qatorni bekorga tortardi. Alohida marshrut o'z `staleTime` iga
 * ega bo'ladi.
 */
export const TREND_STALE_MS = 10 * 60_000;

/**
 * Davomat trendi (hero kartadagi sparkline).
 *
 * ⚠️ **`to` UZATILADI, faqat sana tanlangan bo'lsa.** Sanasiz holatda
 * `to` YUBORILMAYDI — server SOZLANGAN zonadagi (`settings.timezone`)
 * bugunni hisoblaydi, server OS zonasidagi emas
 * (`attendance.repository.ts` ning `businessDateInZone()` i). Klient
 * `dayjs()` dan sana qursa, boshqa zonadagi foydalanuvchi (VPN, UTC
 * kiosk) jimgina BOSHQA biznes-kunni ko'rardi.
 *
 * ⚠️ Kesh kalitiga `TREND_DAYS` ham kiradi: oyna uzunligi o'zgarsa
 * javob ham boshqacha bo'ladi va eski javob qayta ishlatilmasligi
 * kerak. `undefined` ni kalit sifatida ishlatib bo'lmaydi —
 * `DASHBOARD_AUTO_KEY` bilan bir xil qoida.
 */
export function useAttendanceTrend(date?: string) {
	const { can } = usePermission();

	return useQuery<AttendanceTrend>({
		queryKey: ["dashboard-trend", date ?? DASHBOARD_AUTO_KEY, TREND_DAYS],
		queryFn: () => dashboardApi.trend(TREND_DAYS, date),
		enabled: can("attendance", "read"),
		staleTime: TREND_STALE_MS,
	});
}

/**
 * «N soniya oldin yangilandi» matni, har soniyada tiklanadi.
 *
 * ⚠️ Hook FAQAT TIKADI — butun mantiq `formatYangilangan` sof
 * funksiyasida va u `DASHBOARD_POLL_MS` ni UMUMAN bilmaydi: matn oxirgi
 * MUVAFFAQIYATLI javobdan hisoblanadi, poll oralig'idan emas. Poll
 * yiqilganda hisoblagich o'sishi kerak — aynan shunda u ogohlantirishga
 * aylanadi.
 */
export function useRefreshedAgo(dataUpdatedAt: number): string {
	const { t } = useTranslation("dashboard");
	const [now, setNow] = useState(() => Date.now());

	useEffect(() => {
		const id = setInterval(() => setNow(Date.now()), 1_000);

		return () => clearInterval(id);
	}, []);

	return formatYangilangan(dataUpdatedAt, now, t);
}

export function useRiskyEmployees() {
	const { can } = usePermission();

	return useQuery<RiskyEmployee[]>({
		queryKey: ["risky-employees"],
		queryFn: () => dashboardApi.risky(),
		enabled: can("employees", "read"),
		refetchInterval: RISKY_POLL_MS,
	});
}

/**
 * «Ko'rib chiqdim» belgisi.
 *
 * ⚠️ Muvaffaqiyatdan keyin `dashboard` ham invalidatsiya qilinadi —
 * `xavfliKorilmagan` sanog'i aynan shu amaldan o'zgaradi va u faqat
 * keyingi poll'da yangilansa, rahbar belgini qo'yib bo'lib ham eski
 * raqamni ko'rib turardi.
 *
 * ⚠️ Bayroqning O'ZI o'zgarmaydi (kontrakt §7.5) — u hisoblanadi.
 */
export function useAcknowledgeRisk() {
	const { message } = App.useApp();
	const { t } = useTranslation("dashboard");
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, reason }: { id: string; reason: string }) =>
			dashboardApi.acknowledge(id, reason),
		onSuccess: () => {
			message.success(t("messages.acknowledgeSuccess"));
			queryClient.invalidateQueries({ queryKey: ["risky-employees"] });
			queryClient.invalidateQueries({ queryKey: ["dashboard"] });
		},
		onError: (error) => {
			message.error(getErrorMessage(error, t("messages.acknowledgeError")));
		},
	});
}
